/* eslint-disable replo/no-tailwind-literal-color */
import type { ReactFile } from "schemas/generated/reactFile";

import React, { useEffect } from "react";

import { selectProjectId } from "@editor/reducers/core-reducer";
import { useEditorSelector } from "@editor/store";
import { trpc } from "@editor/utils/trpc";

import {
  SandpackCodeEditor,
  SandpackFileExplorer,
  SandpackLayout,
  SandpackPreview,
  SandpackProvider,
} from "@codesandbox/sandpack-react";
import Button from "@replo/design-system/components/button/Button";
import { skipToken } from "@tanstack/react-query";

import buttonContents from "../base-files/components/ui/button?raw";
import productContextContents from "../base-files/context/product?raw";
import editableListenerContents from "../base-files/editableListener?raw";
import libUtilsContents from "../base-files/lib/utils?raw";
import { useReactRenderStore } from "../store/reactRenderStore";
import { getSandpackFilePath } from "../utils/reactFilesUtils";

function ReactRender() {
  const projectId = useEditorSelector(selectProjectId);
  const {
    openPages,
    selectedElement,
    setSelectedElement,
    setEditText,
    displayMode,
    setOpenPages,
  } = useReactRenderStore();

  const { data, isLoading: isLoadingReactFiles } = trpc.reactFile.list.useQuery(
    projectId ? { projectId } : skipToken,
  );
  const reactFiles = data?.reactFiles ?? [];

  useEffect(() => {
    if (selectedElement) {
      setEditText(selectedElement.text);
    }
    if (selectedElement === null) {
      // Find the iframe (adjust selector as needed)
      const iframe = document.querySelector("iframe");
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage({ type: "CLEAR_SELECTION" }, "*");
      }
    }
  }, [selectedElement, setEditText]);
  const [isDebugModeOn, setIsDebugModeOn] = React.useState(false);

  const convertFilesToSandpackFiles = (files: ReactFile[]) => {
    const fileSystem = files.reduce((acc, file) => {
      const filePath = getSandpackFilePath(file);
      return {
        ...acc,
        [filePath]: file.content,
      };
    }, {});

    return {
      ...fileSystem,
      ...createDefaultAppFile(openPages, files, displayMode),
      "/src/lib/utils.ts": libUtilsContents,
      "/src/components/ui/button.tsx": buttonContents,
      "/src/context/products.ts": productContextContents,
      ...(displayMode === "edit"
        ? { "/editableListener.ts": editableListenerContents }
        : {}),
    };
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === "ELEMENT_SELECTED") {
        setSelectedElement(event.data.payload);
      }
    };

    void window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [setSelectedElement]);

  if (isLoadingReactFiles) {
    return <div>Loading...</div>;
  }

  const scale = 1;

  return (
    <div className="flex h-full w-full flex-col">
      <div className="flex items-center gap-2">
        <Button
          onClick={() => setIsDebugModeOn(!isDebugModeOn)}
          variant={isDebugModeOn ? "secondary" : "primary"}
        >
          {isDebugModeOn ? "Debug On" : "Debug Off"}
        </Button>
      </div>
      <div className="relative h-full w-full overflow-hidden">
        <div
          style={{
            // transform: `scale(${scale})`,
            transformOrigin: "top left",
            width: `${100 / scale}%`,
            height: `${100 / scale}%`,
            overflow: "auto",
          }}
          className="flex h-full w-full flex-col"
        >
          <SandpackProvider
            className="flex !h-full !w-full flex-col"
            key={openPages.join(",") + displayMode}
            options={{
              externalResources: ["https://cdn.tailwindcss.com"],
            }}
            customSetup={{
              dependencies: {
                "react-router-dom": "6.22.3",
                "@radix-ui/react-slot": "1.2.0",
                "@radix-ui/react-icons": "1.3.2",
                "class-variance-authority": "0.7.0",
                clsx: "2.0.0",
                "tailwind-merge": "3.2.0",
                "lucide-react": "0.344.0",
              },
              entry: "/src/index.tsx",
            }}
            files={convertFilesToSandpackFiles(reactFiles)}
            template="react-ts"
          >
            <SandpackLayout className="flex h-full w-full flex-col">
              <div className="flex h-full w-full flex-col">
                {/* DEBUG HELPER  */}
                {isDebugModeOn && (
                  <div className="max-h-[30rem] overflow-y-auto">
                    <SandpackCodeEditor showTabs={false} />
                    <SandpackFileExplorer />
                    <div className="my-3 space-y-2">
                      <div className="text-sm font-medium text-">
                        Available Components:
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {reactFiles.map((file) => {
                          const isSelected =
                            openPages.includes(file.relativePath) &&
                            file.fileType === "page";

                          if (!file.exportName) {
                            return null;
                          }
                          return (
                            <button
                              key={file.fileName}
                              onClick={() => {
                                if (isSelected) {
                                  // Remove from openPages if already selected
                                  setOpenPages(
                                    openPages.filter(
                                      (page) => page !== file.relativePath,
                                    ),
                                  );
                                } else {
                                  setOpenPages([
                                    ...openPages,
                                    file.relativePath,
                                  ]);
                                }
                              }}
                              className={`rounded-md px-3 py-1 text-sm transition-colors ${
                                isSelected
                                  ? "border border-blue-300 bg-blue-100 text-blue-700"
                                  : "border border-gray-200 bg-gray-100 text-gray-700 hover:bg-gray-200"
                              }`}
                            >
                              {file.exportName}
                            </button>
                          );
                        })}
                      </div>
                      {openPages.length > 0 && (
                        <div className="text-sm text-gray-500">
                          Selected:{" "}
                          {openPages.map((page) => (
                            <div className="px-3" key={page}>
                              {page}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {/* END DEBUG HELPER */}

                <div className="flex h-full w-full flex-col overflow-y-auto">
                  {openPages.map((path, index) => (
                    <>
                      <SandpackPreview
                        key={index}
                        showOpenInCodeSandbox={false}
                        showRefreshButton={false}
                        startRoute={path}
                        className="h-full w-full rounded-none"
                      />
                    </>
                  ))}
                </div>
              </div>
            </SandpackLayout>
          </SandpackProvider>
        </div>
      </div>
    </div>
  );
}

const findPageFileFromPath = (path: string, files: ReactFile[]) => {
  return files.find((f) => f.relativePath === path && f.fileType === "page");
};

const createDefaultAppFile = (
  openPages: string[],
  files: ReactFile[],
  displayMode: "preview" | "edit",
) => {
  return {
    "/App.tsx": `import React from 'react';
  import { BrowserRouter, Routes, Route } from 'react-router-dom';
  ${displayMode === "edit" ? `import './editableListener';` : ""}
  ${openPages
    .map((path) => {
      const pageFile = findPageFileFromPath(path, files);
      if (!pageFile) {
        return null;
      }
      return `import ${pageFile?.exportName} from '.${getSandpackFilePath(pageFile)}';`;
    })
    .join("\n")}

  const App: React.FC = () => {
  return (
      <BrowserRouter>
      <Routes>
  ${openPages
    .map((path) => {
      const pageFile = findPageFileFromPath(path, files);
      if (!pageFile) {
        return null;
      }
      return `        <Route path="${path}" element={<${pageFile?.exportName} />} />`;
    })
    .join("\n")}
      </Routes>
      </BrowserRouter>
  );
  };

  export default App;`,
  };
};

export default ReactRender;
